import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useControlledState } from '@prophecy/utils/react/hooks';
import React from 'react';
import { CheckPenIcon, ChevronRightIcon } from '../../Icons';
import { Stack } from '../../Layout';
import { TabVariant } from '../../Tabs';
import { theme } from '../../theme';
import { Tooltip } from '../../Tooltip';
import { Text } from '../../Typography';
import { StyledTabsRoot, StyledTabsList, StepIconContainer } from '../sharedStyled';
import { tokens } from '../tokens';
import { StepSize, StepState } from '../types';
import { getStepState } from '../utils';
import { HorizontalStepBase, HorizontalSeparator, HorizontalNoWrapStackItem, StyledStepNumber } from './styled';
export function HorizontalStepper({ current, defaultCurrent, onChange, steps, filled, size = StepSize.sm, separator = /*#__PURE__*/ _jsx(ChevronRightIcon, {
    type: "default",
    size: "xs",
    color: tokens.Separator.color
}), ...rest }) {
    const [currentStep = 0, setCurrentStep] = useControlledState({
        value: current,
        defaultValue: defaultCurrent,
        onChange
    });
    const onTabChange = (value)=>{
        setCurrentStep(Number(value));
    };
    const renderStepIcon = (index, state, disabled)=>{
        if (state === StepState.completed) {
            return /*#__PURE__*/ _jsx(StepIconContainer, {
                $size: size,
                children: /*#__PURE__*/ _jsx(CheckPenIcon, {
                    type: "extended"
                })
            });
        }
        return /*#__PURE__*/ _jsx(StyledStepNumber, {
            level: size,
            $size: size,
            tone: disabled ? tokens.StepList.horizontal.state.disabled.color : tokens.StepList.horizontal.state[state].color,
            children: /*#__PURE__*/ _jsxs("b", {
                children: [
                    index + 1,
                    "."
                ]
            })
        });
    };
    const renderStepContent = (label, _details, state, disabled)=>/*#__PURE__*/ _jsx(HorizontalNoWrapStackItem, {
            children: /*#__PURE__*/ _jsx(Text, {
                level: size,
                weight: theme.fontWeight.semiBold,
                tone: disabled ? tokens.StepList.horizontal.state.disabled.color : tokens.StepList.horizontal.state[state].color,
                children: label
            })
        });
    const renderSeparator = (index)=>{
        if (index >= steps.length - 1) return null;
        return /*#__PURE__*/ _jsx(HorizontalSeparator, {
            children: separator
        });
    };
    const renderStep = (step, index)=>{
        const { title, label, details, ...stepProps } = step;
        const state = getStepState(index, currentStep);
        const disabled = stepProps.disabled;
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Tooltip, {
                    title: title,
                    children: /*#__PURE__*/ _jsx(HorizontalStepBase, {
                        ...stepProps,
                        value: index.toString(),
                        $state: state,
                        style: {
                            height: '100%'
                        },
                        children: /*#__PURE__*/ _jsxs(Stack, {
                            direction: "horizontal",
                            alignY: "center",
                            gap: tokens.StepList.horizontal.gap,
                            children: [
                                renderStepIcon(index, state, !!disabled),
                                renderStepContent(label, details, state, !!disabled)
                            ]
                        })
                    })
                }),
                renderSeparator(index)
            ]
        }, index);
    };
    return /*#__PURE__*/ _jsx(StyledTabsRoot, {
        value: currentStep.toString(),
        onValueChange: onTabChange,
        dir: "ltr",
        variant: TabVariant.underline,
        $orientation: "horizontal",
        ...rest,
        children: /*#__PURE__*/ _jsx(StyledTabsList, {
            $orientation: "horizontal",
            children: /*#__PURE__*/ _jsx(Stack, {
                direction: "horizontal",
                alignY: "center",
                gap: tokens.StepList.horizontal.gap,
                children: steps.map(renderStep)
            })
        })
    });
}
